import { Avatar, SelectInput, SelectInputItem } from '@ads/components-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/shared/ui/atoms';

import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { getStudentInitials } from '../../../utils/getStudentInitials';

/** Uma opção do seletor de plano, já com o rótulo resolvido. */
export interface ConsolidationPlanOption {
  readonly id: number;
  readonly label: string;
}

export interface ConsolidationHeaderCardProps {
  readonly studentName: string | null | undefined;
  readonly email: string | null | undefined;
  /** Exibe skeletons na identificação enquanto o header carrega. */
  readonly isStudentLoading: boolean;
  readonly plans: readonly ConsolidationPlanOption[];
  /** Plano selecionado — sempre o `financialPersonId` da rota. */
  readonly selectedPlanId: number;
  /** `true` desabilita o seletor de plano (lista ainda indisponível). */
  readonly isPlanSelectDisabled: boolean;
  readonly onPlanChange: (planId: number) => void;
  readonly years: readonly number[];
  /** Ano selecionado, ou `null` no estado inicial. */
  readonly selectedYear: number | null;
  readonly onYearChange: (year: number) => void;
}

/**
 * Card de identificação do aluno com os dois seletores da consolidação.
 *
 * Apresentacional: recebe opções e handlers por prop e não chama hook de API —
 * as três requisições vivem na página, que é quem também traduz a escolha do
 * seletor em navegação.
 *
 * Os campos ausentes degradam para `—` em vez de sumir: com o card colapsando
 * de altura conforme o header responde, a régua abaixo dele saltaria na tela.
 */
export function ConsolidationHeaderCard({
  studentName,
  email,
  isStudentLoading,
  plans,
  selectedPlanId,
  isPlanSelectDisabled,
  onPlanChange,
  years,
  selectedYear,
  onYearChange,
}: ConsolidationHeaderCardProps) {
  const { t } = useTranslation('individualPayment');

  return (
    <section className="flex flex-wrap items-center justify-between gap-xs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-xxs py-xxs">
      <div className="flex min-w-none items-center gap-micro">
        {isStudentLoading ? (
          <>
            <Skeleton className="h-sm w-sm rounded-circular" />
            <div className="flex flex-col gap-quark">
              <Skeleton className="h-xs w-[11rem] rounded-sm" />
              <Skeleton className="h-mini w-[13rem] rounded-sm" />
            </div>
          </>
        ) : (
          <>
            <Avatar size="md" fallback={getStudentInitials(studentName)} hasPlaceholder />
            <div className="flex min-w-none flex-col">
              <span className="break-words ts-paragraph-xs !font-medium text-ctx-content-title">
                {studentName ?? EMPTY_VALUE}
              </span>
              <span className="break-words ts-caption-xxs text-ctx-content-placeholder">
                {email ?? EMPTY_VALUE}
              </span>
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-start gap-micro">
        <div className="w-[24rem] max-w-full">
          <SelectInput
            label={t('consolidation.plan.label')}
            size="md"
            fullWidth
            placeholder={t('consolidation.plan.placeholder')}
            disabled={isPlanSelectDisabled}
            value={String(selectedPlanId)}
            onValueChange={value => onPlanChange(Number(value))}
          >
            {plans.map(plan => (
              <SelectInputItem
                custom={{ root: 'text-nowrap' }}
                key={plan.id}
                value={String(plan.id)}
              >
                {plan.label}
              </SelectInputItem>
            ))}
          </SelectInput>
        </div>

        <div className="w-[9rem] max-w-full">
          <SelectInput
            label={t('consolidation.year.label')}
            size="md"
            fullWidth
            placeholder={t('consolidation.year.placeholder')}
            disabled={years.length === 0}
            value={selectedYear === null ? '' : String(selectedYear)}
            onValueChange={value => onYearChange(Number(value))}
          >
            {years.map(year => (
              <SelectInputItem custom={{ root: 'text-nowrap' }} key={year} value={String(year)}>
                {String(year)}
              </SelectInputItem>
            ))}
          </SelectInput>
        </div>
      </div>
    </section>
  );
}
